import { useEffect, useState } from "react";
import ProGate from "@/components/ProGate";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Plus, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function CodesInner() {
  const [codes, setCodes] = useState(null);
  const [subs, setSubs] = useState(null);
  const [form, setForm] = useState({ code: "", maxUses: 1, note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/codes").then(({ data }) => setCodes(data)).catch(() => toast.error("Could not load codes"));
    api.get("/admin/subscribers").then(({ data }) => setSubs(data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/codes", { code: form.code || null, maxUses: Number(form.maxUses), note: form.note });
      toast.success("Code created");
      setForm({ code: "", maxUses: 1, note: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => {
    await api.patch(`/admin/codes/${c.code}`, { active: !c.active });
    load();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="admin-codes-page">
      <div className="flex items-center gap-2 mb-6"><KeyRound className="h-5 w-5 text-amber-400" /><h1 className="font-display text-2xl sm:text-3xl font-extrabold">Lifetime codes</h1></div>

      <Card className="bg-[#111723] border-[#222F43] p-5 mb-6">
        <form onSubmit={create} className="grid sm:grid-cols-[1fr_110px_1fr_auto] gap-2 items-end">
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Code (blank = random)</label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CADDYOG" className="bg-[#0B0F17] border-[#334566] font-mono" data-testid="code-input" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Uses</label>
            <Input type="number" min={1} value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} className="bg-[#0B0F17] border-[#334566] font-mono" data-testid="code-uses-input" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Note</label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Who's it for?" className="bg-[#0B0F17] border-[#334566]" data-testid="code-note-input" /></div>
          <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="code-create-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Create</>}
          </Button>
        </form>
      </Card>

      <div className="space-y-2 mb-10">
        {codes === null && <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />}
        {codes?.map((c) => (
          <Card key={c.code} className="bg-[#111723] border-[#222F43] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`code-row-${c.code}`}>
            <div className="font-mono font-bold text-lg tracking-wider">{c.code}</div>
            <Badge variant="outline" className={`font-mono ${c.uses >= c.maxUses ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`} data-testid={`code-uses-${c.code}`}>{c.uses}/{c.maxUses} used</Badge>
            <div className="text-xs text-slate-400 flex-1">{c.note}</div>
            {c.redeemedBy?.length > 0 && <div className="text-[11px] text-slate-500 font-mono truncate max-w-[260px]">{c.redeemedBy.map((r) => r.email).join(", ")}</div>}
            <div className="flex items-center gap-2 text-xs text-slate-400"><Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`code-toggle-${c.code}`} /> {c.active ? "active" : "disabled"}</div>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-emerald-400" /><h2 className="font-display text-xl font-bold">Members</h2></div>
      <Card className="bg-[#111723] border-[#222F43] p-4 text-sm" data-testid="members-card">
        {!subs ? <Loader2 className="h-5 w-5 animate-spin text-emerald-400" /> : (
          <>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Subscriptions ({subs.subscriptions.length})</div>
            {subs.subscriptions.length === 0 && <div className="text-slate-500 text-xs mb-3">None yet.</div>}
            {subs.subscriptions.map((s) => (
              <div key={s.user_id} className="flex flex-wrap gap-3 py-1.5 border-b border-[#1a2333] last:border-0 font-mono text-xs">
                <span className="text-slate-300">{s.user_id}</span>
                <Badge variant="outline" className="border-[#334566]">{s.status}</Badge>
                <span className="text-slate-500">{s.lookup_key}</span>
                <span className="text-slate-500">{s.cancel_at_period_end ? "cancels" : "renews"} {s.current_period_end?.slice(0, 10)}</span>
              </div>
            ))}
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-4 mb-2">Lifetime ({subs.lifetime.length})</div>
            {subs.lifetime.map((u) => <div key={u.email} className="font-mono text-xs text-slate-300 py-1">{u.email} <span className="text-slate-500">· {u.lifetime_code}</span></div>)}
          </>
        )}
      </Card>
    </div>
  );
}

export default function AdminCodes() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <div className="max-w-md mx-auto px-4 py-24 text-center text-slate-400">Admins only.</div>;
  return <ProGate feature="Admin"><CodesInner /></ProGate>;
}
